import moment from 'moment-timezone';
import { TripStopTimeWindowStatuses } from '../store/config/enums/tripStatusFilter.enum';
import { Stop, Trip, TripArrangeStop, TripWithStopsDetails } from '../store/config/types/trips.types';
import { Customer } from '../store/config/types';
import { TripScoreType } from '../components/TripsScoreCard';
import { AttachMoney, DataUsage, FormatAlignJustify } from '@material-ui/icons';

export function isAlliveTrip(trip: Trip) {
  return ['IN_TRANSIT', 'NEW', 'PENDING'].includes(trip.status);
}

export function getTimeWindowStatus(
  stop: Stop | TripArrangeStop,
  tripStatus: string = 'NEW',
  tripStart?: Date,
  timezone?: string,
  computedMode?: boolean,
  ignoreDueLate?: boolean,
): TripStopTimeWindowStatuses {
  const tz = timezone ? moment.tz(timezone).format('ZZ') : '';
  const delayedMinutes = ignoreDueLate
    ? 0
    : Math.max(['NEW', 'PENDING'].includes(tripStatus) && tripStart ? moment().diff(tripStart, 'minute') : 0, 0);
  const start = moment(
    computedMode
      ? stop.computedArrivalAt
      : ('schedulerArrivalAt' in stop ? stop.schedulerArrivalAt : undefined) ?? stop.routerArrivalAt,
  ).add(delayedMinutes, 'minutes');
  const end = moment(
    computedMode
      ? stop.computedDepartureAt
      : ('schedulerDepartureAt' in stop ? stop.schedulerDepartureAt : undefined) ?? stop.routerDepartureAt,
  ).add(delayedMinutes, 'minutes');
  const timeWindowFrom = moment(
    `${start.format('YYYY-MM-DD')}T${stop.timeWindowFrom ?? '00:00:00'}${tz}`,
    'YYYY-MM-DDTHH:mm:ssZ',
  );
  const timeWindowTo = moment(
    `${end.format('YYYY-MM-DD')}T${stop.timeWindowTo ?? '23:59:59'}${tz}`,
    'YYYY-MM-DDTHH:mm:ssZ',
  );

  if (!timeWindowTo && !timeWindowFrom) return TripStopTimeWindowStatuses.TIME_WINDOW_UNDEFINED;

  if (
    (start.diff(timeWindowFrom, 'minutes') >= 0 && start.diff(timeWindowTo, 'minutes') <= 0) ||
    (end.diff(timeWindowFrom, 'minutes') >= 0 && end.diff(timeWindowTo, 'minutes') <= 0)
  )
    return TripStopTimeWindowStatuses.TIME_WINDOW_IN_RANGE;
  else return TripStopTimeWindowStatuses.TIME_WINDOW_WARNING;
}

export const stopSortBySchedulerTimes = (a: Stop | TripArrangeStop, b: Stop | TripArrangeStop) =>
  moment(a.schedulerArrivalAt ?? a.routerArrivalAt).unix() - moment(b.schedulerArrivalAt ?? b.routerArrivalAt).unix();

export function convertTripToTripWithStopsDetails(trip?: Trip): TripWithStopsDetails | null {
  if (!trip) return null;
  return {
    ...(trip as unknown as TripWithStopsDetails),
    DSP: trip.dspJob?.deliveryServiceProvider,
    dspRefrenceId: trip.dspJob?.dspReferenceId,
  };
}

export const stopSortByComputedTimes = (a: Stop | TripArrangeStop, b: Stop | TripArrangeStop) => {
  return moment(a.computedArrivalAt).unix() - moment(b.computedArrivalAt).unix();
};

/* eslint-disable */
export const filterByComputedDate = (
  a: Stop | TripArrangeStop,
  index: number,
  allStops: Stop[] | TripArrangeStop[],
) => {
  return Boolean(a.computedArrivalAt);
};
/* eslint-enable */

export const customerLabel = (customer?: Customer, customerIndex?: number) => {
  return customer && (customer.firstName || customer.lastName)
    ? `${(customer.firstName ? customer.firstName : ' ')[0]} ${(customer.lastName ? customer.lastName : ' ')[0]}`
    : customer?.companyName
    ? `${customer.companyName[0]}`
    : `C${customerIndex ?? ''}`;
};
export function getTripStopSearchString(stop: TripArrangeStop) {
  const customer = stop.location.customer;
  const searchComponents: string[] = [];
  if (stop.location?.streetAddress) searchComponents.push(stop.location.streetAddress);
  if (stop.shipToAddressee) searchComponents.push(stop.shipToAddressee);
  if (customer) {
    if (customer.location?.streetAddress) {
      searchComponents.push(customer?.location?.streetAddress);
    }
    [customer.companyName, customer.firstName, customer.lastName].forEach((info) => {
      if (info) searchComponents.push(info);
    });
  }
  return searchComponents.join(' ');
}

export function calculateTripScores(currTrips?: TripWithStopsDetails[], orgTrips?: TripWithStopsDetails[]) {
  const scores: TripScoreType[] = [];
  const tripsCount = currTrips?.length ?? 0;
  let utilizationsAverage = 0;
  let vehicleCount = 0;
  currTrips?.forEach((trip) => {
    if (trip.vehicle) {
      const packCount = trip?.tripStops?.length
        ? (trip?.tripStops as any[]).reduce(
            (total: number, s: any, idx: number) => (idx ? total + (s.packageCount ?? s.stopPackages?.length ?? 0) : 0),
            0,
          )
        : 0;
      const utilization = trip?.vehicle?.capacity ? ((packCount ?? 0) / trip.vehicle.capacity) * 100 : 0;
      utilizationsAverage += Math.min(utilization, 100);
      vehicleCount++;
    }
  });
  utilizationsAverage /= vehicleCount ? vehicleCount : 1;
  let orgUtilizationsAverage = 0;
  vehicleCount = 0;
  orgTrips?.forEach((trip) => {
    if (trip.vehicle) {
      const packCount = trip?.tripStops?.length
        ? (trip?.tripStops as any[]).reduce(
            (total: number, s: any, idx: number) => (idx ? total + (s.packageCount ?? s.stopPackages?.length ?? 0) : 0),
            0,
          )
        : 0;
      const utilization = trip?.vehicle?.capacity ? ((packCount ?? 0) / trip.vehicle.capacity) * 100 : 0;
      orgUtilizationsAverage += Math.min(utilization, 100);
      vehicleCount++;
    }
  });
  orgUtilizationsAverage /= vehicleCount ? vehicleCount : 1;

  const totalCost = (currTrips ?? ([] as any[])).reduce(
    (total, trip) => total + (trip.additionalData?.totalCost ?? 0),
    0,
  );
  const orgTotalCost = (orgTrips ?? ([] as any[])).reduce(
    (total, trip) => total + (trip.additionalData?.totalCost ?? 0),
    0,
  );

  scores.push(
    {
      id: 'NUMBER_OF_TRIPS',
      title: 'Number of trips',
      caption: `Trip${tripsCount > 1 ? 's' : ''}`,
      number: currTrips?.length ?? 0,
      icon: FormatAlignJustify,
      originalNumber: orgTrips?.length ?? 0,
      indicatorType: 'lower-is-best',
    },
    {
      id: 'AVERAGE_UTILIZATION',
      title: 'Average Utilization',
      caption: `%`,
      number: utilizationsAverage ? utilizationsAverage : 0,
      icon: DataUsage,
      originalNumber: orgUtilizationsAverage,
      indicatorType: 'greater-is-best',
    },
    {
      id: 'TOTAL_COST',
      title: 'Total Cost',
      caption: ``,
      number: totalCost,
      icon: AttachMoney,
      originalNumber: orgTotalCost,
      indicatorType: 'lower-is-best',
    },
  );

  return scores;
}
